// Gear.jsx
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { gearTeethPath, gearSpokesPath, ringPoints } from './gearGeometry';

/**
 * A single industrial gear rendered entirely in SVG.
 *
 * @param {number}      cx        centre x (user units)
 * @param {number}      cy        centre y (user units)
 * @param {number}      rOuter    tooth-tip radius
 * @param {number}      teeth     number of teeth
 * @param {number}      spokes    number of mechanical spokes
 * @param {string}      idPrefix  unique prefix for gradient / filter ids
 * @param {MotionValue} rotate    rotation in degrees (scroll-driven)
 * @param {MotionValue} glow      0..1 energy intensity (scroll-driven)
 * @param {number}      pulseDelay css animation delay for the rim pulse (s)
 */
function Gear({
  cx,
  cy,
  rOuter,
  teeth = 24,
  spokes = 6,
  idPrefix,
  rotate,
  glow,
  pulseDelay = 0,
}) {
  const geo = useMemo(() => {
    const rTip = rOuter;
    const rRoot = rOuter * 0.86;
    const rRingOut = rOuter * 0.8;
    const rRingIn = rOuter * 0.62;
    const rHub = rOuter * 0.27;
    const rHubIn = rOuter * 0.16;
    const rBolts = rOuter * 0.71;
    const rRimPulse = rOuter * 0.91;

    return {
      rTip,
      rRoot,
      rRingOut,
      rRingIn,
      rHub,
      rHubIn,
      rBolts,
      rRimPulse,
      teethPath: gearTeethPath(cx, cy, rTip, rRoot, teeth),
      spokePath: gearSpokesPath(cx, cy, rHub, rRingIn, spokes, 0.19, 0.11),
      bolts: ringPoints(cx, cy, rBolts, spokes, Math.PI / spokes),
    };
  }, [cx, cy, rOuter, teeth, spokes]);

  const id = (suffix) => `${idPrefix}-${suffix}`;
  const rimCirc = 2 * Math.PI * geo.rRimPulse;

  return (
    <g>
      <defs>
        {/* Brushed-metal body */}
        <linearGradient id={id('metal')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fbfcfe" />
          <stop offset="22%" stopColor="#d9dee7" />
          <stop offset="50%" stopColor="#aeb6c4" />
          <stop offset="74%" stopColor="#cad0db" />
          <stop offset="100%" stopColor="#8b94a6" />
        </linearGradient>

        {/* Inner ring band — slightly cooler */}
        <radialGradient id={id('ring')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="60%" stopColor="#c4cad6" />
          <stop offset="88%" stopColor="#9aa3b4" />
          <stop offset="100%" stopColor="#7d8698" />
        </radialGradient>

        {/* Centre hub — domed metal */}
        <radialGradient id={id('hub')} cx="0.38" cy="0.34" r="0.75">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="38%" stopColor="#d2d8e2" />
          <stop offset="78%" stopColor="#9ba4b5" />
          <stop offset="100%" stopColor="#6f788b" />
        </radialGradient>

        {/* Travelling purple energy gradient for the rim pulse */}
        <linearGradient id={id('energy')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#6D28D9" />
          <stop offset="45%" stopColor="#8B5CF6" />
          <stop offset="75%" stopColor="#A855F7" />
          <stop offset="100%" stopColor="#C084FC" />
        </linearGradient>

        {/* Soft mechanical drop shadow */}
        <filter id={id('shadow')} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="#1f2433" floodOpacity="0.22" />
        </filter>

        {/* Purple bloom for energised elements */}
        <filter id={id('bloom')} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <motion.g
        style={{
          rotate,
          transformBox: 'fill-box',
          transformOrigin: 'center',
          willChange: 'transform',
        }}
      >
        {/* Toothed body */}
        <path
          d={geo.teethPath}
          fill={`url(#${id('metal')})`}
          stroke="#737d90"
          strokeWidth="1"
          strokeLinejoin="round"
          filter={`url(#${id('shadow')})`}
        />

        {/* Outer rim accent line */}
        <circle cx={cx} cy={cy} r={geo.rRoot - 2} fill="none" stroke="#ffffff" strokeOpacity="0.5" strokeWidth="1" />

        {/* Inner ring band */}
        <circle cx={cx} cy={cy} r={geo.rRingOut} fill={`url(#${id('ring')})`} stroke="#828b9d" strokeWidth="1" />
        <circle cx={cx} cy={cy} r={geo.rRingIn} fill="#ffffff" />
        <circle cx={cx} cy={cy} r={geo.rRingIn} fill={`url(#${id('metal')})`} fillOpacity="0.65" />
        <circle cx={cx} cy={cy} r={geo.rRingIn} fill="none" stroke="#8d96a8" strokeWidth="1" />

        {/* Bolt / lightening detail holes */}
        {geo.bolts.map((b, i) => (
          <g key={i}>
            <circle cx={b.x} cy={b.y} r={rOuter * 0.055} fill="#7d8698" />
            <circle cx={b.x} cy={b.y} r={rOuter * 0.03} fill="#eef1f6" />
          </g>
        ))}

        {/* Mechanical spokes */}
        <path
          d={geo.spokePath}
          fill={`url(#${id('metal')})`}
          stroke="#828b9d"
          strokeWidth="1"
          strokeLinejoin="round"
        />

        {/* Illuminated spokes overlay (energy travelling inside the gear) */}
        <motion.path
          d={geo.spokePath}
          fill="none"
          stroke={`url(#${id('energy')})`}
          strokeWidth="2.5"
          strokeLinejoin="round"
          filter={`url(#${id('bloom')})`}
          style={{ opacity: glow }}
        />

        {/* Centre hub */}
        <circle cx={cx} cy={cy} r={geo.rHub} fill={`url(#${id('hub')})`} stroke="#6f788b" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r={geo.rHubIn} fill="#eef1f6" stroke="#9aa3b4" strokeWidth="1" />

        {/* Hub energy core — glows as energy is processed */}
        <motion.circle
          cx={cx}
          cy={cy}
          r={geo.rHubIn}
          fill={`url(#${id('energy')})`}
          filter={`url(#${id('bloom')})`}
          style={{ opacity: glow }}
        />
      </motion.g>

      {/* Circumference energy — travels continuously around the rim, intensity from scroll.
          Rendered OUTSIDE the rotating group so the light flows independently of teeth. */}
      <motion.circle
        className="gear-rim-pulse"
        cx={cx}
        cy={cy}
        r={geo.rRimPulse}
        fill="none"
        stroke={`url(#${id('energy')})`}
        strokeWidth="3"
        strokeLinecap="round"
        filter={`url(#${id('bloom')})`}
        pathLength={1}
        strokeDasharray="0.22 0.78"
        style={{ opacity: glow, ['--pulse-delay']: `${pulseDelay}s` }}
      />
    </g>
  );
}

export default React.memo(Gear);
